import { BaseService } from '../base/base.service';

export interface AdminDashboardServiceStatus {
  name: string;
  status: string;
  url?: string;
}

export interface AdminDashboardSnapshot {
  totalServices: number;
  healthyServices: number;
  unhealthyServices: number;
  systemConfigurations: number;
  activeAdmins: number;
  recentAuditLogs: number;
  lastUpdated: string;
  services: AdminDashboardServiceStatus[];
}

export interface AdminUserStatistics {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  generatedAt: string;
}

type RawServiceStatus = {
  Name?: string;
  Status?: string;
  Url?: string;
  name?: string;
  status?: string;
  url?: string;
};

type RawDashboardResponse = {
  TotalServices?: number;
  HealthyServices?: number;
  UnhealthyServices?: number;
  SystemConfigurations?: number;
  ActiveAdmins?: number;
  RecentAuditLogs?: number;
  LastUpdated?: string;
  Services?: RawServiceStatus[];
  totalServices?: number;
  healthyServices?: number;
  unhealthyServices?: number;
  systemConfigurations?: number;
  activeAdmins?: number;
  recentAuditLogs?: number;
  lastUpdated?: string;
  services?: RawServiceStatus[];
};

type RawUserStatisticsResponse = {
  TotalUsers?: number;
  ActiveUsers?: number;
  InactiveUsers?: number;
  NewUsersToday?: number;
  NewUsersThisWeek?: number;
  NewUsersThisMonth?: number;
  GeneratedAt?: string;
  totalUsers?: number;
  activeUsers?: number;
  inactiveUsers?: number;
  newUsersToday?: number;
  newUsersThisWeek?: number;
  newUsersThisMonth?: number;
  generatedAt?: string;
};

const resolveNumber = (value: number | undefined, fallback = 0) =>
  typeof value === 'number' ? value : fallback;

const resolveString = (value: string | undefined, fallback = '') =>
  typeof value === 'string' ? value : fallback;

class AdminDashboardService extends BaseService {
  constructor() {
    super('/api/admin');
  }

  async getDashboard(): Promise<AdminDashboardSnapshot> {
    const response = await this.get<RawDashboardResponse>('/dashboard');
    const rawServices = response.services ?? response.Services ?? [];

    const services = rawServices.map((service) => ({
      name: service.name ?? service.Name ?? 'Unknown',
      status: service.status ?? service.Status ?? 'Unknown',
      url: service.url ?? service.Url,
    }));

    return {
      totalServices: resolveNumber(
        response.totalServices ?? response.TotalServices,
        services.length
      ),
      healthyServices: resolveNumber(
        response.healthyServices ?? response.HealthyServices
      ),
      unhealthyServices: resolveNumber(
        response.unhealthyServices ?? response.UnhealthyServices
      ),
      systemConfigurations: resolveNumber(
        response.systemConfigurations ?? response.SystemConfigurations
      ),
      activeAdmins: resolveNumber(response.activeAdmins ?? response.ActiveAdmins),
      recentAuditLogs: resolveNumber(
        response.recentAuditLogs ?? response.RecentAuditLogs
      ),
      lastUpdated: resolveString(
        response.lastUpdated ?? response.LastUpdated,
        new Date().toISOString()
      ),
      services,
    };
  }

  async getUserStatistics(): Promise<AdminUserStatistics> {
    const response = await this.get<RawUserStatisticsResponse>(
      '/user-profile/statistics'
    );

    return {
      totalUsers: resolveNumber(response.totalUsers ?? response.TotalUsers),
      activeUsers: resolveNumber(response.activeUsers ?? response.ActiveUsers),
      inactiveUsers: resolveNumber(
        response.inactiveUsers ?? response.InactiveUsers
      ),
      newUsersToday: resolveNumber(
        response.newUsersToday ?? response.NewUsersToday
      ),
      newUsersThisWeek: resolveNumber(
        response.newUsersThisWeek ?? response.NewUsersThisWeek
      ),
      newUsersThisMonth: resolveNumber(
        response.newUsersThisMonth ?? response.NewUsersThisMonth
      ),
      generatedAt: resolveString(
        response.generatedAt ?? response.GeneratedAt,
        new Date().toISOString()
      ),
    };
  }
}

export const adminDashboardService = new AdminDashboardService();
